import React, { useState, useEffect } from 'react';
import { colors } from '../../utils/colors';
import type { Teacher, TeacherAttendanceRecord } from '../../utils/types';
import SlideToSubmit from '../../components/SlideToSubmit';

interface Props {
  teacher: Teacher;
  record: TeacherAttendanceRecord | null;
  onSave: (record: TeacherAttendanceRecord) => void;
  onBack: () => void;
}

type Status = 'present' | 'absent' | 'late';

const todayISO  = () => new Date().toISOString().split('T')[0];
const now       = () => new Date().toISOString();
const formatFullDate = () =>
  `Today - ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })}`;

const OPTIONS: { value: Status; label: string; color: string; bg: string; border: string }[] = [
  { value: 'present', label: 'I am present', color: '#166534', bg: '#f0fdf4', border: '#86efac' },
  { value: 'late',    label: 'I am late',    color: '#92400e', bg: '#fff7ed', border: '#fcd34d' },
  { value: 'absent',  label: 'On leave',     color: '#0e7490', bg: '#ecfeff', border: '#67e8f9' },
];

const TeacherAttendance: React.FC<Props> = ({ record, onSave, onBack }) => {
  const [selected, setSelected] = useState<Status | null>(record?.status ?? null);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(!!record);

  useEffect(() => {
    if (record) { setSelected(record.status); setSaved(true); }
  }, [record]);

  const isEditable = !record || record.date === todayISO();

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    await new Promise(r => setTimeout(r, 700));
    onSave({ date: todayISO(), status: selected, markedAt: now() });
    setSaving(false);
    setSaved(true);
    onBack();
  };

  const currentOpt = OPTIONS.find(o => o.value === selected);

  return (
    <div style={{
      height: '100dvh', background: '#fff',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
    }}>
      {/* Header */}
      <div style={{
        padding: '52px 20px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.borderLight}`,
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 16, fontWeight: 500, color: colors.text,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          My attendance
        </button>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
          stroke={colors.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8"  y1="2" x2="8"  y2="6"/>
          <line x1="3"  y1="10" x2="21" y2="10"/>
          <rect x="8" y="14" width="2" height="2" rx="0.5" fill={colors.primary}/>
        </svg>
      </div>

      {/* Scrollable body */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '20px 20px 130px',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: colors.textMuted,
          letterSpacing: '0.8px', marginBottom: 4,
        }}>
          LOG ATTENDANCE
        </div>

        <div style={{
          background: colors.primaryDark, borderRadius: 12,
          padding: '13px 18px', fontSize: 15, fontWeight: 600, color: '#fff',
        }}>
          {formatFullDate()}
        </div>

        {saved && !isEditable ? (
          <div style={{
            background: currentOpt?.bg ?? '#f8fafc',
            border: `1.5px solid ${currentOpt?.border ?? colors.border}`,
            borderRadius: 14, padding: '18px', textAlign: 'center', marginTop: 8,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: currentOpt?.color ?? colors.text }}>
              Attendance Locked
            </div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 6 }}>
              You marked yourself as <strong>{currentOpt?.label}</strong> today.
            </div>
          </div>
        ) : (
          OPTIONS.map(opt => {
            const active = selected === opt.value;
            return (
              <button key={opt.value} onClick={() => !saved && setSelected(opt.value)} style={{
                width: '100%', padding: '16px 18px',
                background: active ? opt.bg : '#fff',
                border: `1.5px solid ${active ? opt.border : colors.border}`,
                borderRadius: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: saved ? 'default' : 'pointer',
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: active ? opt.color : colors.textMuted }}>
                  {opt.label}
                </span>
                <div style={{
                  width: 24, height: 24, borderRadius: 6,
                  border: `2px solid ${active ? opt.color : colors.border}`,
                  background: active ? opt.color : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s',
                }}>
                  {active && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* 
        Bottom fade + button.
        Gradient layer fades #fff from transparent → solid, giving a clean
        "fade out" effect that works on every platform without backdropFilter.
      */}
      <div style={{
        position: 'absolute',
        left: 0, right: 0, bottom: 0,
        zIndex: 20,
        pointerEvents: 'none',
      }}>
        {/* Gradient fade layer */}
        <div style={{
          height: 48,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
          pointerEvents: 'none',
        }} />
        {/* Solid button area */}
        <div style={{
          background: '#fff',
          padding: '0 20px 32px',
          pointerEvents: 'all',
        }}>
          {!saved && (
            <SlideToSubmit
              onConfirm={handleConfirm}
              disabled={!selected || saving}
              label="Slide to confirm"
              confirmedLabel="Attendance Saved!"
            />
          )}
          {saved && isEditable && (
            <button onClick={() => setSaved(false)} style={{
              width: '100%', padding: '15px',
              background: colors.primaryDark, color: '#fff',
              border: 'none', borderRadius: 14,
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>
              Change Status
            </button>
          )}
          {saved && !isEditable && (
            <div style={{
              textAlign: 'center', fontSize: 13,
              color: colors.textMuted, padding: '8px 0',
            }}>
              Attendance locked for today.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherAttendance;